import {
  IonAlert,
  IonButton,
  IonIcon,
  IonImg,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonNote,
  IonPopover,
  IonRadio,
  IonRadioGroup,
  IonSpinner,
  IonText,
  IonTextarea,
  IonToggle,
} from '@ionic/react';
import {
  add,
  cloudUpload,
  crop as cropIcon,
  pencil,
  sparkles,
  trash,
} from 'ionicons/icons';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { ImageGenerationService } from '../../../services/ImageGeneration.service';
import { extractDominantColor } from '../../../utils/colorExtraction';
import { getCroppedImage } from '../../../utils/imageUtils';
import { getFramePath } from '../../Icons';
import { once, run } from '../../../helper/electron';
import { useAssetPath } from '../../../hooks/useAssetPath';
import ApiService from '../../../services/Api.service';
import isElectron from '../../../utils/isElectron';
import '../Crates.scss';

type FrameType = 'circle' | 'square' | 'rect' | 'none';

const FRAME_FILES: Record<Exclude<FrameType, 'none'>, string> = {
  circle: 'frame_circle.png',
  square: 'frame_square.png',
  rect: 'frame_rect.png',
};

interface ImageCropperProps {
  editable: boolean;
  currentImageUrl?: string;
  crateTitle?: string;
  onImageChange: (croppedFile: File | null) => void;
  onColorExtracted?: (color: string | null) => void;
}

const ImageCropper: React.FC<ImageCropperProps> = ({
  editable,
  currentImageUrl,
  crateTitle,
  onImageChange,
  onColorExtracted,
}) => {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | undefined>(
    currentImageUrl || undefined,
  );
  const [showCropModal, setShowCropModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [cropAreaPixels, setCropAreaPixels] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [originalImage, setOriginalImage] = useState<string | undefined>(
    undefined,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generatePrompt, setGeneratePrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const isImageGenerationAvailable = ImageGenerationService.isConfigured();
  const [flaggedTerms, setFlaggedTerms] = useState<string[]>([]);
  const [showFlaggedConfirm, setShowFlaggedConfirm] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
  const [includeWatermark, setIncludeWatermark] = useState(true);
  const [autoMatchGradient, setAutoMatchGradient] = useState(true);
  const [userGroups, setUserGroups] = useState<any[]>([]);

  const [frameType, setFrameType] = useState<FrameType>('none');
  const frameSrc = useAssetPath(
    frameType === 'none' ? '' : `frames/${FRAME_FILES[frameType]}`,
  );

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const chAPI = ApiService.getApiClient();
        const groups = await chAPI.usersControllerGetUsersGroups();
        setUserGroups(groups || []);
      } catch {
        setUserGroups([]);
      }
    };
    fetchGroups();
  }, []);

  // Check if user is a Crate Hackers member
  const crateHackersGroup = useMemo(() => {
    return userGroups.find((g) => g.id === 1 || g.name === 'Crate Hackers');
  }, [userGroups]);
  const defaultPrompt = useMemo(() => {
    if (crateTitle?.trim()) {
      return `${crateTitle.trim()} vibes, energetic mood, colorful atmosphere`;
    }
    return 'Energetic party atmosphere, vibrant colors, lifestyle photography';
  }, [crateTitle]);

  const extractColorFromImage = async (imageUrl: string) => {
    if (!autoMatchGradient || !onColorExtracted) return;

    try {
      const colorInfo = await extractDominantColor(imageUrl);
      onColorExtracted(colorInfo.hex);
    } catch (error) {
      console.error('Failed to extract color:', error);
    }
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const objectUrl = URL.createObjectURL(file);
    setOriginalImage(undefined);

    // Preload image to ensure it's ready before updating the state
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = objectUrl;
    img.onload = () => {
      setOriginalImage(objectUrl);
      setShowCropModal(true);
      setShowMenu(false);
    };

    // In case there's an error loading the image
    img.onerror = (event) => {
      console.error('❌ Error loading image preview:', img.src);
      setOriginalImage(undefined);
    };
    // Reset File input value
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
    setCropAreaPixels(croppedAreaPixels);
  };

  const handleCropSave = async () => {
    if (!originalImage) return;

    try {
      if (!cropAreaPixels) return;
      const croppedFile = await getCroppedImage(originalImage, cropAreaPixels);

      if (!croppedFile || croppedFile.size === 0) {
        console.error('Cropped image is empty or invalid');
        return;
      }

      const previewUrl = URL.createObjectURL(croppedFile);

      setImageFile(croppedFile);
      setPreviewImage(previewUrl);
      setShowCropModal(false);
      setShowMenu(false);

      if (frameType !== 'none' && isElectron()) {
        try {
          const url = await getFramePath(FRAME_FILES[frameType]);
          const res: Response = await fetch(url);
          if (!res.ok) {
            throw new Error(`Failed to load frame: ${res.status}`);
          }
          const blob: Blob = await res.blob();
          const frame = new File([blob], FRAME_FILES[frameType], {
            type: blob.type,
          });

          const [baseFile, overlayFile] = await Promise.all([
            croppedFile.arrayBuffer(),
            frame.arrayBuffer(),
          ]);

          const framedFile = await new Promise<File | null>((resolve) => {
            const timeout = setTimeout(() => resolve(null), 15000);
            once('overlay-frame', (response: any) => {
              clearTimeout(timeout);
              if (!response?.success || !response.data) {
                resolve(null);
                return;
              }
              const framedBlob = new Blob([response.data], {
                type: 'image/png',
              });
              const baseName = croppedFile.name.replace(/\.[^/.]+$/, '');
              resolve(
                new File([framedBlob], `${baseName}-${frameType}_frame.png`, {
                  type: 'image/png',
                }),
              );
            });
            run('overlay-frame', {
              baseFile,
              overlayFile,
            });
          });

          onImageChange(framedFile ?? croppedFile);
        } catch (overlayError) {
          console.error('Error overlaying frame:', overlayError);
          onImageChange(croppedFile);
        }
      } else {
        onImageChange(croppedFile);
      }
      extractColorFromImage(previewUrl);
    } catch (error) {
      console.error('Error cropping image:', error);
    }
  };

  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    fileInputRef.current?.click();
    setShowMenu(false);
  };

  const handleRemoveImage = () => {
    setPreviewImage(undefined);
    setImageFile(null);
    onImageChange(null);
    if (onColorExtracted) {
      onColorExtracted(null);
    }
    setShowMenu(false);
  };

  const handleEditExisting = () => {
    if (previewImage) {
      setOriginalImage(previewImage);
      setShowCropModal(true);
      setShowMenu(false);
    }
  };

  const performImageGeneration = async (promptValue: string) => {
    if (!isImageGenerationAvailable) {
      setGenerationError('Image generation endpoint is not configured.');
      return;
    }

    if (!promptValue.trim()) {
      setGenerationError('Please enter a prompt to generate album art.');
      return;
    }

    setIsGenerating(true);
    setGenerationError(null);

    try {
      const file = await ImageGenerationService.generateImage({
        prompt: promptValue.trim(),
        crateTitle,
        aspectRatio: '1:1',
        includeWatermark,
      });

      if (previewImage && previewImage.startsWith('blob:')) {
        URL.revokeObjectURL(previewImage);
      }

      const generatedUrl = URL.createObjectURL(file);

      setImageFile(file);
      setPreviewImage(generatedUrl);
      setOriginalImage(generatedUrl);
      onImageChange(file);
      extractColorFromImage(generatedUrl);
      setShowGenerateModal(false);
      setShowMenu(false);

      setTimeout(() => {
        setShowCropModal(true);
      }, 150);
    } catch (error) {
      console.error('Image generation error:', error);
      setGenerationError(
        error instanceof Error
          ? error.message
          : 'Unable to generate image. Please try again.',
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const flaggedKeywordList = useMemo(
    () => [
      'nsfw',
      'explicit',
      'nude',
      'nudity',
      'strip',
      'violence',
      'blood',
      'gore',
      'weapon',
      'drugs',
      'politics',
      'controversial',
      'gun',
      'knife',
      'adult only',
      'child',
      'teen',
      'minor',
      'underage',
    ],
    [],
  );

  const findFlaggedTerms = (value: string) => {
    const lowered = value.toLowerCase();
    return flaggedKeywordList.filter((term) => lowered.includes(term));
  };

  const requestImageGeneration = (value: string) => {
    const trimmed = value.trim();
    const flagged = findFlaggedTerms(trimmed);

    if (flagged.length > 0) {
      setFlaggedTerms(flagged);
      setPendingPrompt(trimmed);
      setShowFlaggedConfirm(true);
      return;
    }

    performImageGeneration(trimmed);
  };

  const confirmFlaggedGeneration = () => {
    if (pendingPrompt) {
      performImageGeneration(pendingPrompt);
    }
    setShowFlaggedConfirm(false);
    setPendingPrompt(null);
  };

  const cancelFlaggedGeneration = () => {
    setShowFlaggedConfirm(false);
    setPendingPrompt(null);
  };

  useEffect(() => {
    if (showGenerateModal && !generatePrompt.trim()) {
      setGeneratePrompt(defaultPrompt);
      setGenerationError(null);
    }
  }, [showGenerateModal, generatePrompt, defaultPrompt]);

  useEffect(() => {
    setPreviewImage(currentImageUrl || undefined);
    if (currentImageUrl && autoMatchGradient) {
      extractColorFromImage(currentImageUrl);
    }
  }, [currentImageUrl, autoMatchGradient]);

  return (
    <div className="crate-image-cropper">
      {previewImage ? (
        <IonImg className="preview-img" alt="Crate Cover" src={previewImage} />
      ) : editable ? (
        <div className="image-placeholder">
          <IonIcon icon={cloudUpload} />
          <IonText>Add Cover Image</IonText>
        </div>
      ) : null}

      {editable && previewImage && (
        <IonButton
          fill="clear"
          id="edit-image"
          onClick={() => setShowMenu(true)}
        >
          <IonIcon icon={pencil} slot="start" />
          <IonText className="ion-padding-start">Edit Image</IonText>
        </IonButton>
      )}

      {editable && !previewImage && (
        <IonButton
          fill="clear"
          className="add-image-button"
          onClick={() => setShowMenu(true)}
        >
          <IonIcon icon={add} slot="start" />
          Add Image
        </IonButton>
      )}
      <IonPopover
        isOpen={showMenu}
        onDidDismiss={() => setShowMenu(false)}
        alignment="start"
        side="right"
        trigger="edit-image"
        className="crate-image-popover"
      >
        <IonList lines="none">
          <IonItem button onClick={openFileDialog}>
            <IonIcon icon={cloudUpload} slot="start" />
            {previewImage ? 'Upload New Image' : 'Upload Image'}
          </IonItem>
          {previewImage && (
            <>
              <IonItem button onClick={handleEditExisting}>
                <IonIcon icon={cropIcon} slot="start" />
                Crop Image
              </IonItem>
              <IonItem button onClick={handleRemoveImage}>
                <IonIcon icon={trash} slot="start" color="danger" />
                <IonText color="danger">Remove Image</IonText>
              </IonItem>
            </>
          )}
          <IonItem
            button
            disabled={!isImageGenerationAvailable}
            onClick={() => {
              setShowGenerateModal(true);
              setShowMenu(false);
            }}
          >
            <IonIcon icon={sparkles} slot="start" />
            Generate with AI
          </IonItem>
          {!isImageGenerationAvailable && (
            <IonItem lines="none" className="menu-helper-text">
              <IonNote color="medium">
                Set REACT_APP_OPENAI_API_KEY (or
                REACT_APP_IMAGE_GENERATION_ENDPOINT) to enable AI art.
              </IonNote>
            </IonItem>
          )}
        </IonList>
      </IonPopover>

      {/* Hidden File Input */}
      <input
        type="file"
        accept=".png, .jpg, .jpeg"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleImageSelect}
      />

      <IonModal isOpen={showCropModal}>
        <div
          style={{
            width: '100%',
            height: '400px',
            position: 'relative',
          }}
        >
          {originalImage ? (
            <Cropper
              image={originalImage}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropComplete={handleCropComplete}
              onCropChange={setCrop}
              onZoomChange={setZoom}
            />
          ) : (
            <IonText>Please select an image first</IonText>
          )}

          {frameType !== 'none' && (
            <img
              src={frameSrc}
              alt="frame"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: 400,
                height: 400,
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                zIndex: 10,
              }}
            />
          )}
        </div>
        {crateHackersGroup && isElectron() && (
          <IonRadioGroup
            value={frameType}
            onIonChange={(e) => setFrameType(e.detail.value)}
          >
            <div className="frame-editor-frame-select">
              <h3 className="frame-editor-title">Select Frame :</h3>
              {['circle', 'square', 'rect', 'none'].map((type) => (
                <IonItem key={type} lines="none" button>
                  <IonRadio slot="end" style={{ margin: '5px' }} value={type}>
                    {' '}
                    <IonLabel>{type.toUpperCase()}</IonLabel>
                  </IonRadio>
                </IonItem>
              ))}
            </div>
          </IonRadioGroup>
        )}

        <IonButton color="success" fill="outline" onClick={handleCropSave}>
          Save
        </IonButton>
        <IonButton
          color="danger"
          fill="outline"
          onClick={() => setShowCropModal(false)}
        >
          Cancel
        </IonButton>
      </IonModal>

      <IonModal
        isOpen={showGenerateModal}
        onDidDismiss={() => {
          setShowGenerateModal(false);
          setGenerationError(null);
        }}
      >
        <div className="crate-image-generate-modal">
          <IonText className="generate-modal-title">
            <strong>Generate Album Art</strong>
          </IonText>
          <IonTextarea
            autoGrow
            label="Prompt"
            labelPlacement="stacked"
            value={generatePrompt}
            onIonChange={(event) => setGeneratePrompt(event.detail.value || '')}
            placeholder="Describe the mood or scene (e.g., sunset beach party, neon city lights, abstract colorful shapes)"
          />

          <IonItem lines="none">
            <IonLabel>Include Crate Hackers logo</IonLabel>
            <IonToggle
              checked={includeWatermark}
              onIonChange={(e) => setIncludeWatermark(e.detail.checked)}
            />
          </IonItem>

          <IonItem lines="none">
            <IonLabel>Match header color to image</IonLabel>
            <IonToggle
              checked={autoMatchGradient}
              onIonChange={(e) => setAutoMatchGradient(e.detail.checked)}
            />
          </IonItem>
          {generationError && (
            <IonNote color="danger">{generationError}</IonNote>
          )}

          <div className="generate-modal-actions">
            <IonButton
              color="medium"
              fill="clear"
              onClick={() => {
                setShowGenerateModal(false);
                setGenerationError(null);
              }}
            >
              Cancel
            </IonButton>
            <IonButton
              color="primary"
              onClick={() => requestImageGeneration(generatePrompt)}
              disabled={isGenerating}
            >
              {isGenerating && <IonSpinner slot="start" />}Generate
            </IonButton>
          </div>
        </div>
      </IonModal>

      <IonAlert
        isOpen={showFlaggedConfirm}
        header="Sensitive Prompt"
        message={`Your prompt includes the following sensitive terms: ${flaggedTerms.join(
          ', ',
        )}. Are you sure you want to continue?`}
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
            handler: cancelFlaggedGeneration,
          },
          {
            text: 'Continue',
            role: 'confirm',
            handler: confirmFlaggedGeneration,
          },
        ]}
        onDidDismiss={() => {
          setShowFlaggedConfirm(false);
        }}
      />
    </div>
  );
};

export default ImageCropper;
